import { Channel, channels, media, Media } from 'lib-shared-types-channels';
import memoizeOne from 'memoize-one';

import { getChannelMedia } from 'utils/channels';
import {
  FeatureFlagName,
  featureFlags,
} from 'utils/featureFlagging/featureFlags';

const channelToFeatureFlagMap: ReadonlyMap<Channel, FeatureFlagName> = new Map([
  ['adachat', 'ada-chat-channel'],
  ['callback', 'phone-channel'],
  ['contact_form', 'email-channel'],
  ['email', 'email-channel'],
  ['facebook_messenger', 'facebook-messenger-channel'],
  ['messenger', 'has-messenger'],
  ['pstn_phone', 'phone-channel'],
  ['sms', 'sms-channel'],
  ['twitter', 'twitter-channel'],
  ['voicemail', 'phone-channel'],
  ['whats_app', 'whatsapp-channel'],
  ['widget_chat', 'widget-chat-channel'],
]);

/**
 * Helper function that maps channel names to the corresponding feature flag
 * name that should control access to the channel it.
 *
 * @param channel The channel to get the feature flag for
 * @returns Feature flag name for the channel
 * @throws Error if the channel is not found in the map
 */
export function getFeatureFlagNameByChannel(channel: Channel): FeatureFlagName {
  if (channelToFeatureFlagMap.has(channel)) {
    const featureFlag = channelToFeatureFlagMap.get(channel);
    if (featureFlag) return featureFlag;
  }

  throw new Error(`No feature flag found for channel: ${channel}`);
}

/**
 * Goes through all the channel feature flags and calculates the media it
 * belongs to should be enabled according to feature flags.
 *
 * @param channelFeatureFlags
 * @returns Record of each media and their enabled status.
 */
export const getMediaFeatureFlagStatus = memoizeOne((): Map<Media, boolean> => {
  const channelFeatureFlags = getChannelFeatureFlagsStatus();
  const mediaFeatureFlagMap = new Map<Media, boolean>(
    media.map((mediaName) => [mediaName, false]),
  );
  for (const channel of channels) {
    try {
      const media = getChannelMedia(channel);
      if (channelFeatureFlags.get(channel)) {
        mediaFeatureFlagMap.set(media, true);
      }
    } catch (e) {
      // Feature flag or media not found - do nothing
      console.warn(e);
    }
  }
  return mediaFeatureFlagMap;
});

/**
 * Return a list of each channel and whether it should be enabled according to
 * the channel feature flags.
 *
 * @returns Map of each channel and whether it is enabled by feature flags.
 */
export const getChannelFeatureFlagsStatus = memoizeOne(
  (): Map<Channel, boolean> => {
    const channelFeatureFlagMap = new Map<Channel, boolean>(
      channels.map((channel) => [channel, false]),
    );
    for (const channel of channels) {
      try {
        const featureFlagName = getFeatureFlagNameByChannel(channel);
        const [featureFlagValue] = Object.values(
          featureFlags.get([featureFlagName]),
        );
        channelFeatureFlagMap.set(channel, featureFlagValue as boolean);
      } catch (e) {
        // Feature flag not found - assume it it should not be enabled
        console.warn(e);
      }
    }
    return channelFeatureFlagMap;
  },
);
